/**
 * The whole taxonomy as one picture (docs/en/43 sec.8): the four roots' templates, the value
 * types, and every directive, with what joins them - a template extends another, a field
 * refers to a template or holds a value, a link runs between two, a directive applies to one.
 *
 * <p>Read from the definition as it is written, the draft being worked on included, so that
 * the picture changes as a person edits it and before anything is saved. Nothing here knows
 * a sector or an application: a package is a list the definition itself keeps.
 */
import type { Locale } from './i18n';
import { labelOf, type FieldDefinition, type RegistryDefinition, type TypeDefinition } from './registry';

/**
 * Where a node is drawn: one lane per root, one for the value types, and the directives in two -
 * what happens (the Process branch: flows, actions, rules...) and what is shown and who may
 * (the View and Access branches: boards, views, screens, packages...). `directive` is kept for
 * a template whose own root is the Directive root.
 */
export type Lane = 'object' | 'event' | 'document' | 'value' | 'process' | 'view' | 'directive';
export const LANES: Lane[] = ['object', 'event', 'document', 'value', 'process', 'view', 'directive'];

/** The collections of a definition that are directives, and what each is called in the tree. */
export const DIRECTIVE_LISTS = ['flows', 'actions', 'rules', 'bindings', 'duplicates', 'approvals', 'notifications', 'conversations',
  'deliveries', 'series', 'retention', 'mappings', 'links',
  'mapLayers', 'boards', 'views', 'screens', 'menu', 'dashboards', 'queries', 'documents', 'messages', 'notificationCategories',
  'configurators', 'packages'] as const;
export type DirectiveList = (typeof DIRECTIVE_LISTS)[number];

/** The branch of the Directive root each kind is (common-lib DirectiveKind): which lane it is drawn in. */
export const BRANCH: Record<DirectiveList, 'process' | 'view' | 'access'> = {
  flows: 'process', actions: 'process', rules: 'process', bindings: 'process', duplicates: 'process',
  approvals: 'process', notifications: 'process', conversations: 'process', deliveries: 'process', series: 'process',
  retention: 'process', mappings: 'process', links: 'process',
  // GeoNexora's map layers (common-lib MapLayerDef, `directive.map-layer`): a View directive about the type it draws.
  mapLayers: 'view', boards: 'view', views: 'view', screens: 'view', menu: 'view', dashboards: 'view', queries: 'view', documents: 'view',
  messages: 'view', notificationCategories: 'view', configurators: 'view', packages: 'access'
};

/** The lane a directive of a kind is drawn in. */
export function laneOfList(list: DirectiveList): Lane {
  return BRANCH[list] === 'process' ? 'process' : 'view';
}

export type NodeKind = 'template' | 'value' | DirectiveList;

export interface TaxNode {
  /** `template:crm.deal`, `boards:board.sales`: unique across the definition. */
  id: string;
  key: string;
  kind: NodeKind;
  lane: Lane;
  label: string;
  /** The template it extends, for a template. */
  parent?: string;
  isAbstract?: boolean;
  /** Which package it is in: `geonexora` for the platform's own. */
  pack: string;
  /** How deep it is under its root, for a template; 0 for anything else. */
  depth: number;
}

export type EdgeKind = 'extends' | 'refers' | 'holds' | 'link' | 'applies' | 'flow' | 'opens' | 'uses';
export const EDGE_KINDS: EdgeKind[] = ['extends', 'refers', 'holds', 'link', 'applies', 'flow', 'opens', 'uses'];

export interface TaxEdge { from: string; to: string; kind: EdgeKind; label?: string }

export interface TaxGraph { nodes: TaxNode[]; edges: TaxEdge[]; byId: Map<string, TaxNode> }

/** What a part in no package of its own is in: the platform's own core. */
export const CORE_PACKAGE = 'geonexora';

type Keyed = { key: string; labels?: Record<string, string>; [other: string]: unknown };

/** A directive list of the definition, as the JSON has it. */
export function listOf(definition: RegistryDefinition | null | undefined, list: DirectiveList): Keyed[] {
  const value = (definition as Record<string, unknown> | null | undefined)?.[list];
  return Array.isArray(value) ? (value as Keyed[]) : [];
}

export function valuesOf(definition: RegistryDefinition | null | undefined): { key: string; labels?: Record<string, string>; fields?: FieldDefinition[] }[] {
  const value = (definition as Record<string, unknown> | null | undefined)?.values;
  return Array.isArray(value) ? value as { key: string; labels?: Record<string, string>; fields?: FieldDefinition[] }[] : [];
}

/** The root a template is under: its own, or its parent's. */
export function rootOf(definition: RegistryDefinition | null | undefined, key: string): string | undefined {
  const types = new Map((definition?.types ?? []).map((t) => [t.key, t]));
  const seen = new Set<string>();
  for (let at = types.get(key); at && !seen.has(at.key); at = at.parent ? types.get(at.parent) : undefined) {
    seen.add(at.key);
    if (at.root) return at.root;
  }
  return undefined;
}

function depthOf(types: Map<string, TypeDefinition>, key: string): number {
  let depth = 0;
  const seen = new Set<string>();
  for (let at = types.get(key); at?.parent && !seen.has(at.key); at = types.get(at.parent)) {
    seen.add(at.key);
    depth++;
  }
  return depth;
}

/** What a part stands on, as the server infers a package's membership from it (docs/en/43 D2). */
function standsOn(list: NodeKind, part: Keyed): string | undefined {
  switch (list) {
    case 'template': return (part as TypeDefinition).parent;
    case 'views': case 'mapLayers': return part.type as string | undefined;
    case 'boards': return part.shows as string | undefined;
    case 'bindings': case 'rules': return part.appliesTo as string | undefined;
    case 'mappings': case 'actions': case 'duplicates': case 'approvals': return part.target as string | undefined;
    case 'queries': return part.from as string | undefined;
    case 'notifications': return (part.on as { type?: string } | undefined)?.type;
    case 'configurators': return part.template as string | undefined;
    case 'links': return part.from as string | undefined;
    default: return undefined;
  }
}

/** Which package a part is in: named by one, or that of what it stands on, or the store's own. */
export function packageOf(definition: RegistryDefinition | null | undefined, list: NodeKind, key: string): string {
  const packages = listOf(definition, 'packages');
  const named = (k: string) => packages.find((p) => ((p.members as string[] | undefined) ?? []).includes(k))?.key;
  const types = new Map((definition?.types ?? []).map((t) => [t.key, t]));
  const seen = new Set<string>();
  let kind: NodeKind = list;
  let at: string | undefined = key;
  while (at && !seen.has(at)) {
    seen.add(at);
    const found = named(at);
    if (found) return found;
    const part: Keyed | undefined = kind === 'template' ? types.get(at) as Keyed | undefined
      : kind === 'value' ? undefined : listOf(definition, kind as DirectiveList).find((p) => p.key === at);
    if (!part) break;
    at = standsOn(kind, part);
    kind = 'template';
  }
  return CORE_PACKAGE;
}

function node(id: string, key: string, kind: NodeKind, lane: Lane, label: string, pack: string, extra: Partial<TaxNode> = {}): TaxNode {
  return { id, key, kind, lane, label, pack, depth: 0, ...extra };
}

export const templateId = (key: string) => `template:${key}`;
export const valueId = (key: string) => `value:${key}`;
export const directiveId = (list: DirectiveList, key: string) => `${list}:${key}`;

/** The definition as nodes and edges. */
export function buildGraph(definition: RegistryDefinition | null | undefined, locale: Locale): TaxGraph {
  const nodes: TaxNode[] = [];
  const edges: TaxEdge[] = [];
  if (!definition) return { nodes, edges, byId: new Map() };
  const types = new Map(definition.types.map((t) => [t.key, t]));

  for (const type of definition.types) {
    const root = rootOf(definition, type.key) ?? 'object';
    const lane: Lane = root === 'event' || root === 'document' || root === 'object' ? root : 'directive';
    nodes.push(node(templateId(type.key), type.key, 'template', lane, labelOf(type.labels, locale, type.key),
      packageOf(definition, 'template', type.key),
      { parent: type.parent, isAbstract: type.abstract === true, depth: depthOf(types, type.key) }));
    if (type.parent) edges.push({ from: templateId(type.key), to: templateId(type.parent), kind: 'extends' });
    for (const field of type.fields ?? []) fieldEdges(templateId(type.key), field, edges);
    if (typeof type.flow === 'string') edges.push({ from: templateId(type.key), to: directiveId('flows', type.flow), kind: 'flow' });
  }
  for (const value of valuesOf(definition)) {
    nodes.push(node(valueId(value.key), value.key, 'value', 'value', labelOf(value.labels, locale, value.key), CORE_PACKAGE));
    for (const field of value.fields ?? []) fieldEdges(valueId(value.key), field, edges);
  }
  for (const list of DIRECTIVE_LISTS) {
    for (const part of listOf(definition, list)) {
      if (list === 'links') {
        // A link is drawn as what it is: a line between two templates. It is chosen by its line.
        edges.push({ from: templateId(part.from as string), to: templateId(part.to as string), kind: 'link', label: part.key });
        continue;
      }
      const id = directiveId(list, part.key);
      nodes.push(node(id, part.key, list, laneOfList(list), labelOf(part.labels, locale, part.key), packageOf(definition, list, part.key)));
      const target = standsOn(list, part);
      if (target) edges.push({ from: id, to: templateId(target), kind: 'applies' });
      for (const [to, label] of directiveUses(list, part)) edges.push({ from: id, to, kind: 'uses', label });
      if (list === 'boards') {
        if (part.flow) edges.push({ from: id, to: directiveId('flows', part.flow as string), kind: 'flow' });
        for (const view of [part.tile, part.opens]) {
          if (typeof view === 'string') edges.push({ from: id, to: directiveId('views', view), kind: 'opens' });
        }
      }
      if (list === 'menu' && typeof part.opens === 'string' && part.opens.startsWith('data:')) {
        edges.push({ from: id, to: templateId(part.opens.slice(5)), kind: 'opens' });
      }
      if (list === 'screens') {
        for (const template of screenTemplates(part.root)) edges.push({ from: id, to: templateId(template), kind: 'applies' });
      }
    }
  }
  // A menu entry names a screen by its path; a retention period the templates it keeps.
  const byPath = new Map(listOf(definition, 'screens').map((s) => [String(s.path ?? ''), directiveId('screens', s.key)]));
  for (const edge of edges) if (edge.to.startsWith('screens-path:')) edge.to = byPath.get(edge.to.slice(13)) ?? edge.to;
  for (const part of listOf(definition, 'retention')) {
    for (const t of (part.appliesTo as string[] | undefined) ?? []) edges.push({ from: directiveId('retention', part.key), to: templateId(t), kind: 'applies' });
  }
  for (const part of listOf(definition, 'actions')) {
    for (const effect of (part.effects as { kind?: string; template?: string }[] | undefined) ?? []) {
      if (effect.kind === 'create' && effect.template) edges.push({ from: directiveId('actions', part.key), to: templateId(effect.template), kind: 'uses', label: 'create' });
    }
  }
  const byId = new Map(nodes.map((n) => [n.id, n]));
  return { nodes, edges: edges.filter((e) => byId.has(e.from) && byId.has(e.to)), byId };
}

/** What a directive names of the other directives: an approval its actions, a dashboard its questions... */
function directiveUses(list: DirectiveList, part: Keyed): [string, string | undefined][] {
  const out: [string, string | undefined][] = [];
  const str = (v: unknown) => (typeof v === 'string' && v ? v : undefined);
  switch (list) {
    case 'approvals': {
      for (const side of ['accept', 'reject'] as const) {
        const action = str((part[side] as { action?: string } | undefined)?.action);
        if (action) out.push([directiveId('actions', action), side]);
      }
      const message = str(part.message);
      if (message) out.push([directiveId('messages', `message.${message}`), undefined]);
      break;
    }
    case 'dashboards':
      for (const widget of (part.widgets as { query?: string; compare?: string }[] | undefined) ?? []) {
        for (const q of [widget.query, widget.compare]) if (str(q)) out.push([directiveId('queries', q!), undefined]);
      }
      break;
    case 'notifications': {
      const category = str(part.category);
      if (category) out.push([directiveId('notificationCategories', category), undefined]);
      const message = str(part.message);
      if (message) out.push([directiveId('messages', `message.${message}`), undefined]);
      break;
    }
    case 'conversations': {
      const retention = str(part.retention);
      if (retention) out.push([directiveId('retention', retention), undefined]);
      break;
    }
    case 'actions':
      for (const effect of (part.effects as { kind?: string; binding?: string; message?: string }[] | undefined) ?? []) {
        if (effect.kind === 'compute' && str(effect.binding)) out.push([directiveId('bindings', effect.binding!), 'compute']);
        if (effect.kind === 'message' && str(effect.message)) out.push([directiveId('messages', `message.${effect.message}`), 'message']);
      }
      break;
    case 'menu': {
      const opens = str(part.opens);
      if (opens?.startsWith('screen:')) out.push([`screens-path:${opens.slice(7)}`, undefined]);
      break;
    }
    default:
  }
  return out;
}

function fieldEdges(from: string, field: FieldDefinition, edges: TaxEdge[]) {
  if (field.type === 'reference' && field.refersTo) edges.push({ from, to: templateId(field.refersTo), kind: 'refers', label: field.key });
  const of = (field as { of?: string }).of;
  if (field.type === 'value' && of) edges.push({ from, to: valueId(of), kind: 'holds', label: field.key });
}

/** The templates a screen's components read. */
function screenTemplates(root: unknown): string[] {
  const out: string[] = [];
  const walk = (n: unknown) => {
    if (!n || typeof n !== 'object') return;
    const data = (n as { data?: { template?: string } }).data;
    if (data?.template) out.push(data.template);
    for (const child of ((n as { children?: unknown[] }).children ?? [])) walk(child);
  };
  walk(root);
  return out;
}

/** The packages there are, the store's own first. */
export function packagesOf(definition: RegistryDefinition | null | undefined): string[] {
  return [CORE_PACKAGE, ...listOf(definition, 'packages').map((p) => p.key)];
}

/** What is joined to a node, either way: its neighbourhood, for highlighting. */
export function neighbours(graph: TaxGraph, id: string): Set<string> {
  const out = new Set<string>([id]);
  for (const edge of graph.edges) {
    if (edge.from === id) out.add(edge.to);
    if (edge.to === id) out.add(edge.from);
  }
  return out;
}

// ------------------------------------------------------------------ where a field is used

export interface FieldUse { where: string; id: string; what: string }

/**
 * Where a template's field is used by a directive (docs/en/43 D10): a board's role, a view's
 * section, a binding's input or output, a rule, a mapping's column. What removing it would
 * break, said before the server says it.
 */
export function usesOf(definition: RegistryDefinition | null | undefined, typeKey: string, field: string): FieldUse[] {
  if (!definition) return [];
  const isUnder = (key: string | undefined) => {
    if (!key) return false;
    const types = new Map(definition.types.map((t) => [t.key, t]));
    const seen = new Set<string>();
    // A directive about the template or one above it sees the field; so does one about a template below it.
    for (let at = types.get(typeKey); at && !seen.has(at.key); at = at.parent ? types.get(at.parent) : undefined) {
      seen.add(at.key);
      if (at.key === key) return true;
    }
    for (let at = types.get(key); at && !seen.has(`below:${at.key}`); at = at.parent ? types.get(at.parent) : undefined) {
      seen.add(`below:${at.key}`);
      if (at.key === typeKey) return true;
    }
    return false;
  };
  const named = (path: unknown) => typeof path === 'string' && (path === field || path.startsWith(`${field}.`));
  const out: FieldUse[] = [];
  for (const board of listOf(definition, 'boards')) {
    if (!isUnder(board.shows as string)) continue;
    for (const [role, path] of Object.entries((board.roles as Record<string, string>) ?? {})) {
      if (named(path)) out.push({ where: board.key, id: directiveId('boards', board.key), what: `role ${role}` });
    }
  }
  for (const view of listOf(definition, 'views')) {
    if (!isUnder(view.type as string)) continue;
    for (const section of ((view.sections as { key: string; items: string[] }[]) ?? [])) {
      if (section.items.some(named)) out.push({ where: view.key, id: directiveId('views', view.key), what: `section ${section.key}` });
    }
  }
  for (const binding of listOf(definition, 'bindings')) {
    if (!isUnder(binding.appliesTo as string)) continue;
    for (const [port, input] of Object.entries((binding.inputs as Record<string, { field?: string }>) ?? {})) {
      if (named(input?.field)) out.push({ where: binding.key, id: directiveId('bindings', binding.key), what: `input ${port}` });
    }
    for (const [port, path] of Object.entries((binding.outputs as Record<string, string>) ?? {})) {
      if (named(path)) out.push({ where: binding.key, id: directiveId('bindings', binding.key), what: `output ${port}` });
    }
  }
  const word = new RegExp(`(^|[^A-Za-z0-9_.])${field.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}([^A-Za-z0-9_]|$)`);
  for (const rules of listOf(definition, 'rules')) {
    if (!isUnder(rules.appliesTo as string)) continue;
    for (const rule of ((rules.rules as { set?: string; when?: string; value?: string }[]) ?? [])) {
      if (named(rule.set) || word.test(rule.when ?? '') || word.test(rule.value ?? '')) {
        out.push({ where: rules.key, id: directiveId('rules', rules.key), what: `rule ${rule.set ?? ''}` });
      }
    }
  }
  for (const mapping of listOf(definition, 'mappings')) {
    if (!isUnder(mapping.target as string)) continue;
    for (const column of ((mapping.columns as { to?: string }[]) ?? [])) {
      if (named(column.to)) out.push({ where: mapping.key, id: directiveId('mappings', mapping.key), what: 'column' });
    }
  }
  return out;
}

// ------------------------------------------------------------------ the tree

export interface TreeEntry { id: string; label: string; key: string; children: TreeEntry[]; isAbstract?: boolean }

/** The taxonomy as a tree: each root's templates under their parents, the values, each kind of directive. */
export function treeOf(graph: TaxGraph): { group: Lane | DirectiveList; entries: TreeEntry[] }[] {
  const out: { group: Lane | DirectiveList; entries: TreeEntry[] }[] = [];
  const templates = graph.nodes.filter((n) => n.kind === 'template');
  const make = (n: TaxNode): TreeEntry => ({
    id: n.id, key: n.key, label: n.label, isAbstract: n.isAbstract,
    children: templates.filter((c) => c.parent === n.key).sort((a, b) => a.key.localeCompare(b.key)).map(make)
  });
  for (const lane of ['object', 'event', 'document', 'directive'] as Lane[]) {
    const tops = templates.filter((n) => n.lane === lane && (!n.parent || !graph.byId.has(templateId(n.parent))));
    if (tops.length > 0) out.push({ group: lane, entries: tops.sort((a, b) => a.key.localeCompare(b.key)).map(make) });
  }
  const values = graph.nodes.filter((n) => n.kind === 'value');
  if (values.length > 0) {
    out.push({ group: 'value', entries: values.map((n) => ({ id: n.id, key: n.key, label: n.label, children: [] })) });
  }
  for (const list of DIRECTIVE_LISTS) {
    const parts = graph.nodes.filter((n) => n.kind === list);
    if (parts.length > 0) {
      out.push({ group: list, entries: parts.map((n) => ({ id: n.id, key: n.key, label: n.label, children: [] })) });
    }
  }
  return out;
}
